// src/pages/Users/Galleries/utils/galleriesUtils.js

import { toAbsolutePhotoUrl } from "../../../../services/albumPhotoServices";

export const ALBUMS_STORAGE_KEY = "albums_ui_state";

export const DEFAULT_ALBUMS_UI = {
  q: "",
  sort: "recent",
  showAll: false,
};

export const FALLBACK_ALBUM_IMG = "/img/no-image.jpg";

export const collatorFR = new Intl.Collator("fr", {
  sensitivity: "base",
});

export function readAlbumsStorage() {
  try {
    const saved = localStorage.getItem(ALBUMS_STORAGE_KEY);

    if (!saved) return DEFAULT_ALBUMS_UI;

    return {
      ...DEFAULT_ALBUMS_UI,
      ...JSON.parse(saved),
    };
  } catch {
    return DEFAULT_ALBUMS_UI;
  }
}

export function strip(value = "") {
  if (value === null || value === undefined) return "";

  return String(value)
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();
}

export function stripHtml(html = "") {
  if (!html) return "";

  const div = document.createElement("div");
  div.innerHTML = html;

  return (div.textContent || div.innerText || "").trim();
}

export function truncateText(value = "", maxLength = 145) {
  const clean = stripHtml(value);

  if (!clean) return "";

  if (clean.length <= maxLength) return clean;

  return `${clean.slice(0, maxLength).trim()}…`;
}

export function safeDateValue(gallery) {
  const date =
    new Date(gallery?.updated_at || gallery?.created_at || 0).getTime() ||
    Number(gallery?.id) ||
    0;

  return date;
}

export function getOrderedPhotos(gallery) {
  const photos = Array.isArray(gallery?.photos)
    ? gallery.photos.filter(Boolean)
    : [];

  return [...photos].sort((a, b) => {
    const positionA = Number.isFinite(Number(a?.position))
      ? Number(a.position)
      : Number.MAX_SAFE_INTEGER;

    const positionB = Number.isFinite(Number(b?.position))
      ? Number(b.position)
      : Number.MAX_SAFE_INTEGER;

    return positionA - positionB || Number(a?.id || 0) - Number(b?.id || 0);
  });
}

export function getCoverPhoto(gallery) {
  const ordered = getOrderedPhotos(gallery);

  const cover = ordered.find((photo) => photo?.is_cover === true);

  if (cover) return cover;
  if (ordered.length) return ordered[0];

  return null;
}

export function getCoverUrl(gallery) {
  const cover = getCoverPhoto(gallery);

  return toAbsolutePhotoUrl(cover?.photo_url || "") || FALLBACK_ALBUM_IMG;
}

export function getPreviewPhotos(gallery, limit = 12) {
  return getOrderedPhotos(gallery)
    .filter((photo) => photo?.photo_url)
    .slice(0, limit)
    .map((photo) => ({
      ...photo,
      absolute_url: toAbsolutePhotoUrl(photo.photo_url),
    }));
}

export function countPhotos(gallery) {
  return getOrderedPhotos(gallery).length;
}

export function filterAndSortGalleries(galleries = [], ui, isManager = false) {
  const safeUi = {
    ...DEFAULT_ALBUMS_UI,
    ...(ui || {}),
  };

  const query = strip(safeUi.q);

  let base = Array.isArray(galleries) ? galleries.filter(Boolean) : [];

  base = base.filter((gallery) => {
    if (!isManager) return !!gallery?.is_active;

    return safeUi.showAll ? true : !!gallery?.is_active;
  });

  if (query) {
    base = base.filter((gallery) => {
      const haystack = [
        gallery?.titre,
        stripHtml(gallery?.description),
      ]
        .map((value) => strip(value))
        .join(" ");

      return haystack.includes(query);
    });
  }

  base.sort((a, b) => {
    if (safeUi.sort === "alpha") {
      return collatorFR.compare(
        String(a?.titre || ""),
        String(b?.titre || "")
      );
    }

    const dateA = safeDateValue(a);
    const dateB = safeDateValue(b);

    if (safeUi.sort === "oldest") {
      return (
        dateA - dateB ||
        collatorFR.compare(String(a?.titre || ""), String(b?.titre || ""))
      );
    }

    return (
      dateB - dateA ||
      collatorFR.compare(String(a?.titre || ""), String(b?.titre || ""))
    );
  });

  return base;
}

export function computeGalleryStats(galleries = []) {
  const safeGalleries = Array.isArray(galleries)
    ? galleries.filter(Boolean)
    : [];

  const totalPhotos = safeGalleries.reduce(
    (total, gallery) => total + countPhotos(gallery),
    0
  );

  const inactiveAlbums = safeGalleries.filter(
    (gallery) => !gallery?.is_active
  ).length;

  return {
    totalPhotos,
    inactiveAlbums,
  };
}

export function hasActiveAlbumFilters(ui, isManager = false) {
  const safeUi = {
    ...DEFAULT_ALBUMS_UI,
    ...(ui || {}),
  };

  return (
    Boolean(safeUi.q) ||
    safeUi.sort !== DEFAULT_ALBUMS_UI.sort ||
    (isManager && safeUi.showAll)
  );
}